import {
	AfterViewInit,
	Component,
	ElementRef,
	Inject,
	OnInit,
	Renderer2,
	ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import {
	AppModalService,
	ModalData,
} from '@datachain/ui-sdk/components/app-modal';
import { randUuid } from '@ngneat/falso';
import { Store } from '@ngrx/store';
import { Map, Set } from 'immutable';
import {
	BehaviorSubject,
	firstValueFrom,
	map,
	Observable,
	startWith,
	takeUntil,
	tap,
} from 'rxjs';

import { ComponentIcons } from '../../../../ui/app-components.icons';
import { DcIcons } from '../../../../ui/app-dc.icons';
import { ValidationErrorKeys } from '../../../../ui/form/validation-erros-keys';
import { ColumnConfigItemEntity } from '../../../domain/column-config-item.entity';
import { ColumnConfigListEntity } from '../../../domain/column-config-list.entity';
import { ColumnModelItemEntity } from '../../../domain/column-model-item.entity';
import {
	SubscriptionDiagnosticError,
	SubscriptionErrorKey,
} from '../../../domain/diagnostic-errors.entity';
import { SubscriptionProvider } from '../../../providers/subscription.provider';
import {
	refreshSubscriptionColumns,
	SubscriptionSelector,
} from '../../../store';
import { ColumnsConfigComponent } from '../../columns-config/columns-config.component';
import {
	ColumnModelAction,
	ColumnModelActionConfirmPopupComponent,
	IColumnModelConfirmationModalData,
} from '../column-model-action-confirm-popup/column-model-action-confirm-popup.component';
import { ColumnModelConfigViewProvider } from './column-model-config-view.provider';
import {
	ColumnModelFormControls,
	ColumnModelMetadataForm,
} from './column-model-metadata.form';

export enum ColumnModelConfigAction {
	Create = 'create',
	Update = 'update',
}

@Component({
	selector: 'app-column-model-config',
	templateUrl: './column-model-config-modal.component.html',
	styleUrls: ['./column-model-config-modal.component.scss'],
	providers: [
		ColumnModelConfigViewProvider,
		ColumnModelMetadataForm,
		SubscriptionProvider,
	],
})
export class ColumnModelConfigModalComponent<
		T extends {
			columnModelConfig: ColumnModelItemEntity;
			availableColumns: Map<string, ColumnConfigItemEntity>;
			action: ColumnModelConfigAction;
		}
	>
	extends DcBaseComponent
	implements OnInit, AfterViewInit
{
	protected readonly DcIcons = DcIcons;
	protected readonly ColumnModelConfigAction = ColumnModelConfigAction;
	protected readonly ColumnConfigFormControls = ColumnModelFormControls;
	protected readonly ValidationErrorKeys = ValidationErrorKeys;
	protected readonly ComponentIcons = ComponentIcons;

	@ViewChild(ColumnsConfigComponent)
	public columnsConfigCmp: ColumnsConfigComponent | null = null;

	public Appearance: MatFormFieldAppearance = 'legacy';

	public vo$: Observable<{
		columns: Map<string, ColumnConfigItemEntity>;
		selectedColumnCount: number;
		action: ColumnModelConfigAction;
		error: SubscriptionDiagnosticError | undefined;
	}>;

	private readonly columns$ = new BehaviorSubject<
		Map<string, ColumnConfigItemEntity>
	>(Map());

	private readonly columnModel$ = new BehaviorSubject<ColumnModelItemEntity>(
		this.modalData.columnModelConfig ?? ColumnModelItemEntity.build()
	);

	private readonly selectedColumnCount$ = new BehaviorSubject<number>(0);

	public constructor(
		private readonly modalService: AppModalService,
		public readonly columnModelConfigForm: ColumnModelMetadataForm,
		private readonly columnModelConfigProvider: ColumnModelConfigViewProvider,
		private readonly subscriptionProvider: SubscriptionProvider,
		private readonly selector: SubscriptionSelector,
		private readonly renderer: Renderer2,
		private readonly elemRef: ElementRef,
		private readonly store: Store,
		@Inject(MAT_DIALOG_DATA) public readonly modalData: ModalData & T
	) {
		super();
		this.cmpId = 'column-modal-config-modal';

		this.columnModelConfigForm.form.valueChanges
			.pipe(
				takeUntil(this.onDestroy$),
				startWith(this.columnModelConfigForm.form.value),
				tap(() => {
					if (!this.columnModelConfigForm.form.valid) {
						this.subscriptionProvider.addCriticalError(
							$localize`:i18n=@@subscription.diagnosticError.columnModel.missingFields:`,
							SubscriptionErrorKey.ColumnModelMissingFields
						);
					} else {
						this.subscriptionProvider.removeError(
							SubscriptionErrorKey.ColumnModelMissingFields
						);
					}
				})
			)
			.subscribe();

		this.vo$ = combineAll({
			columns: this.columns$,
			selectedColumnCount: this.selectedColumnCount$,
			action: this.modalData.action,
			error: this.subscriptionProvider.errors$.pipe(
				map((errors) => errors.valueSeq().first())
			),
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();

		if (
			this.modalData.action === ColumnModelConfigAction.Update &&
			this.modalData.columnModelConfig
		) {
			this.columnModelConfigForm.populate(this.modalData.columnModelConfig);
			this.modalData.columnModelConfig.columns.forEach((col) => {
				const item = this.modalData.availableColumns.get(col.id);

				if (item) {
					this.modalData.availableColumns = this.modalData.availableColumns.set(
						item.id,
						col
					);
				}
			});
		} else if (this.modalData.action === ColumnModelConfigAction.Create) {
			const labelControl = this.columnModelConfigForm.form.get(
				ColumnModelFormControls.Label
			);
			if (labelControl) {
				labelControl.markAsTouched();
			}
		}

		this.columns$.next(this.modalData.availableColumns);
	}

	public override ngAfterViewInit(): void {
		super.ngAfterViewInit();
		setTimeout(
			() =>
				this.columnModelConfigProvider.adjustHeights(
					this.elemRef,
					this.renderer
				),
			750
		);
	}

	public refreshColumnList(): void {
		this.store.dispatch(refreshSubscriptionColumns());
	}

	public async updateColumnList(
		columnList: ColumnConfigListEntity
	): Promise<void> {
		const columnModel = await firstValueFrom(this.columnModel$);

		const selectedColumns = columnList.elements
			.filter((col) => col.isActive)
			.toList()
			.toArray();

		const columnModels = await firstValueFrom(
			this.selector.getSubscriptionColumnModels$()
		);

		const existingColumnModel = columnModels.find((item) =>
			item.isColumnsEqual(Set(selectedColumns))
		);

		if (selectedColumns.length === 0) {
			this.subscriptionProvider.addCriticalError(
				$localize`:i18n=@@subscription.diagnosticError.columnModel.minOneSelected:`,
				SubscriptionErrorKey.NoSelectColumns
			);
		} else {
			this.subscriptionProvider.removeError(
				SubscriptionErrorKey.NoSelectColumns
			);
		}

		if (existingColumnModel && existingColumnModel.id !== columnModel.id) {
			this.subscriptionProvider.addCriticalError(
				$localize`:i18n=@@subscription.diagnosticError.columnModel.exists:`,
				SubscriptionErrorKey.ColumnModelExists
			);
		} else {
			this.subscriptionProvider.removeError(
				SubscriptionErrorKey.ColumnModelExists
			);
		}

		this.selectedColumnCount$.next(selectedColumns.length);
	}

	public async createAction(): Promise<void> {
		if (!this.columnsConfigCmp) {
			return;
		}

		const metadata = this.columnModelConfigForm.extract();

		const columns = await this.columnsConfigCmp.getActiveColumns();

		const columnModel = ColumnModelItemEntity.build({
			id: randUuid(),
			label: metadata?.label,
			description: metadata?.description,
			columns: Set(columns.toList().toArray()),
		});

		this.modalService.closeActiveModal({
			columnModel,
		});
	}

	public async updateAction(): Promise<void> {
		if (!this.columnsConfigCmp || !this.modalData.columnModelConfig) {
			return;
		}

		const metadata = this.columnModelConfigForm.extract();

		const columns = await this.columnsConfigCmp.getActiveColumns();

		const columnModel = ColumnModelItemEntity.build({
			id: this.modalData.columnModelConfig.id,
			label: metadata?.label,
			description: metadata?.description,
			columns: Set(columns.toList().toArray()),
			linkedProfileIds: this.modalData.columnModelConfig.linkedProfileIds,
		});

		const columnsEqual = columnModel.isColumnsEqual(
			this.modalData.columnModelConfig.columns
		);

		/**
		 * Only the column model metadata has been updated.
		 * No changes were made to the columns.
		 */
		if (columnsEqual) {
			return this.modalService.closeActiveModal({
				columnModel,
			});
		}

		/**
		 * The column model has been modified.
		 * We check whether the column model is linked to a profile.
		 */
		if (columnModel.linkedProfileIds.size === 0) {
			return this.modalService.closeActiveModal({
				columnModel,
			});
		}

		const modalConfirm =
			this.modalService.openPopup<IColumnModelConfirmationModalData>(
				ColumnModelActionConfirmPopupComponent,
				{
					title: $localize`:i18n=@@subscription.columnModel.action.confirm.popup.update.header:`,
					subTitle: '',
					columnModelId: this.modalData.columnModelConfig.id,
					element: this.modalData.columnModelConfig.label,
					action: ColumnModelAction.Update,
				},
				{
					disableClose: true,
				}
			);

		const modalConfirmResult = await firstValueFrom(modalConfirm.afterClosed());

		if (!modalConfirmResult) {
			return;
		}

		this.modalService.closeActiveModal({
			columnModel,
		});
	}
}
